/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
import React, { Component, Suspense, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Outlet, useMatches, useParams } from 'react-router';

import { IntlProvider } from 'react-intl';
import { processNode } from 'view-components/utilities/intl';

import Layout from './DifferentLayout';

import IfFeature from './IfFeature';

import AppContainer from './AppContainer';
import DomainInfoLoading from './DomainInfoLoading';
import DomainInfoLoadingFailed from './DomainInfoLoadingFailed';

import { messages } from '../lib/localizedDataLoader';

import { LOCALE } from '../../config/configs';
import { ReduxState } from 'global/types';

type Props = {
  isDomainLoaded: boolean
  domainError: {}
  features: string[]
  isNewLayoutCompatible: boolean
}

const mapStateToProps = ({ domainInfo, breadcrumbs }: ReduxState) => ({
  isDomainLoaded: domainInfo.isLoaded,
  domainError: domainInfo.error,
  breadcrumbs,
});

@connect(mapStateToProps)
class App extends Component<Props> {
  static propTypes = {
    params: PropTypes.object.isRequired,
    isDomainLoaded: PropTypes.bool.isRequired,
    domainError: PropTypes.string,
    features: PropTypes.object,
    breadcrumbs: PropTypes.object.isRequired,
    isNewLayoutCompatible: PropTypes.bool,
  };

  static defaultProps = {
    features: IfFeature.featureData(),
    domainError: null,
    isNewLayoutCompatible: false,
  };

  componentDidMount() {
    this.interval = setInterval(this.setZendeskWidth, 100);
  }

  // The matched page renders through <Outlet/>; which layout wraps it is decided
  // by the route's `isNewLayoutCompatible` handle (resolved in AppRoute below),
  // replacing the v3 `this.props.children.type.isNewLayoutCompatible` check.
  get appView() {
    const { isNewLayoutCompatible } = this.props;
    // Lazy route components (React.lazy) render into this Outlet, so it needs a
    // Suspense boundary; the fallback matches react-loadable's old `loading`.
    const page = (
      <Suspense fallback={<div className="content-view-inner" />}>
        <Outlet />
      </Suspense>
    );

    if (isNewLayoutCompatible) {
      return <Layout {...this.props}>{page}</Layout>;
    }

    return <AppContainer {...this.props}>{page}</AppContainer>;
  }

  render() {
    const { isDomainLoaded, domainError, features } = this.props;
    const { LANGUAGE } = LOCALE;
    let toRender;

    if (isDomainLoaded) {
      if (domainError) {
        toRender = <DomainInfoLoadingFailed message={domainError} />;
      } else if (features) {
        toRender = (
          <IntlProvider
            locale={LANGUAGE}
            messages={processNode({ ...messages[LANGUAGE] })}
          >
            {this.appView}
          </IntlProvider>
        );
      } else {
        toRender = <DomainInfoLoading />;
      }
    } else {
      toRender = <DomainInfoLoading />;
    }

    return toRender;
  }
}

// v8 layout-route element. Supplies the routing data App previously read from
// the v3 `children`/`params`: the deepest matched route's `isNewLayoutCompatible`
// handle and the URL params. Also fires one-time page-view monitoring, which was
// the root route's v3 `onEnter`.
export default function AppRoute() {
  const matches = useMatches();
  const params = useParams();

  useEffect(() => {
  }, []);

  const deepest = matches[matches.length - 1];
  const handle = (deepest && deepest.handle) as { isNewLayoutCompatible?: boolean } | undefined;
  const isNewLayoutCompatible = Boolean(handle && handle.isNewLayoutCompatible);

  return <App params={params} isNewLayoutCompatible={isNewLayoutCompatible} />;
}
